import React, { FunctionComponent, useMemo } from 'react'
import styles from './FeatureBlock.module.scss'
import { FeatureBlockProps } from './FeatureBlock.model'
import {
  AGENDA_OPTIONS,
  FeaturesType,
  INSI_VALIDATION_OPTIONS,
  MSS_PROTOCOLS,
  VITAL_CARD_SERVICE_OPTIONS,
} from '../../../../model/Preference'
import { FormBlock, FormikTextInput, SelectInput, Switch } from '../../../shared'
import RoleRestriction from '../../../../containers/RoleRestriction'
import { Roles } from '../../../../model/Roles'
import { InformationAlert } from '../InformationAlert'
import { isDoctor } from '../../../../misc/user.utilities'
import { ExternalServices } from '../../../../model/User'

export const FeatureBlock: FunctionComponent<FeatureBlockProps> = ({
  user,
  features,
  identityVigilanceOfficerEnabled,
  onIdentityVigilanceOfficerChange,
  onChange,
  onAgendaChange,
  onInsiValidationChange,
  onVitalCardServiceChange,
}) => {
  const hasSephiraAccount = useMemo(
    () =>
      isDoctor(user) &&
      user.externalAccount?.some(
        (externalAccount) => externalAccount.serviceName === ExternalServices.SEPHIRA,
      ),
    [user],
  )
  return (
    <div>
      <FormBlock label="Fonctionnalités" icon="profile">
        <div className={styles.features}>
          <div className={styles.dropdownItem}>
            <SelectInput
              title="Agenda"
              options={AGENDA_OPTIONS}
              value={AGENDA_OPTIONS.find(({ value }) => value === features.agenda)}
              onSelect={(option) => onAgendaChange(option.value)}
              testId="user-features-agenda"
            />
          </div>
          <div className={styles.featuresItem}>
            <SelectInput
              title="Service lecture vitale"
              options={VITAL_CARD_SERVICE_OPTIONS}
              value={VITAL_CARD_SERVICE_OPTIONS.find(
                ({ value }) => value === features.vitalCardService,
              )}
              onSelect={(option) => onVitalCardServiceChange(option.value)}
              testId="user-features-vital_card_service"
            />
          </div>
          {hasSephiraAccount && (
            <InformationAlert>Il y a déjà un compte rattaché à cet utilisateur</InformationAlert>
          )}
          {features.vitalCardService === 'stellair' && (
            <div className={styles.featuresItem}>
              <Switch
                name="Stellair : Validation INSi"
                checked={features.stellairInsiCheck}
                onChange={(checked) => onChange(FeaturesType.stellairInsiCheck, checked)}
                testId="user-features-stellair_insi"
              />
            </div>
          )}
          <div className={styles.featuresItem}>
            <Switch
              name="Aide à la prescription"
              checked={features.drug}
              onChange={(checked) => onChange(FeaturesType.drug, checked)}
              testId="switch-user_features-lap-enable"
            />
          </div>
          {features.drug && (
            <div className={styles.featuresItem}>
              <FormikTextInput
                fieldName="vidalConfiguration.vidalAppId"
                colorPreset="light"
                label="Id Vidal"
              />
              <FormikTextInput
                fieldName="vidalConfiguration.vidalAppKey"
                colorPreset="light"
                label="Clé Vidal"
              />
            </div>
          )}
          <div className={styles.featuresItem}>
            <Switch
              name="Questionnaire à distance"
              checked={features.distantForm}
              onChange={(checked) => onChange(FeaturesType.distantForm, checked)}
              testId="switch-user_features-survey-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Messagerie de santé sécurisée (MSS)"
              checked={features.mail}
              onChange={(checked) => onChange(FeaturesType.mail, checked)}
              testId="switch-user_features-mss-enable"
            />
          </div>
          <div className={styles.dropdownItem}>
            <SelectInput
              title="Protocole MSS"
              options={MSS_PROTOCOLS}
              value={MSS_PROTOCOLS.find(({ value }) => value === features.mssSmtp)}
              onSelect={(option) => onChange(FeaturesType.mssSmtp, option.value)}
              testId="user-features-mss_protocol-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="MSS : Envoi de pièces jointes"
              checked={features.mailAttachments}
              onChange={(checked) => onChange(FeaturesType.mailAttachments, checked)}
              testId="switch-user_features-mss-mail_attachments-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="DMP"
              checked={features.dmp}
              onChange={(checked) => onChange(FeaturesType.dmp, checked)}
              testId="switch-user_features-dmp-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Adel santé"
              checked={features.adel}
              onChange={(checked) => onChange(FeaturesType.adel, checked)}
              testId="switch-user_features-adel-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Module Gynécologie"
              checked={features.gynecology}
              onChange={(checked) => onChange(FeaturesType.gynecology, checked)}
              testId="switch-user_features-gynecology-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Module Audiométrie"
              checked={features.audiometry}
              onChange={(checked) => onChange(FeaturesType.audiometry, checked)}
              testId="switch-user_features-audiometry-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Mises à jour Doctolib"
              checked={features.doctolibUpdates}
              onChange={(checked) => onChange(FeaturesType.doctolibUpdates, checked)}
              testId="switch-user_features-doctolib_updates-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="ePrescription Ordoclic"
              checked={features.ePrescription}
              onChange={(checked) => onChange(FeaturesType.ePrescription, checked)}
              testId="switch-user_features-e_prescription-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Ajout patient destinataire MSS"
              checked={features.addPatientRecipient}
              onChange={(checked) => onChange(FeaturesType.addPatientRecipient, checked)}
              testId="switch-user_features-mss-add_patient_recipient-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Assistant de consultation"
              checked={features.consultationAssistant}
              onChange={(checked) => onChange(FeaturesType.consultationAssistant, checked)}
              testId="switch-user_features-consultation-assistant-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Assistant statistiques"
              checked={features.statsAssistant}
              onChange={(checked) => onChange(FeaturesType.statsAssistant, checked)}
              testId="switch-user_features-stats-assistant-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Dictée vocale"
              checked={features.voiceDictation}
              onChange={(checked) => onChange(FeaturesType.voiceDictation, checked)}
              testId="switch-user_features-voice-dictation-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Debug Dictée vocale"
              checked={features.voiceDictationAdvanced}
              onChange={(checked) => onChange(FeaturesType.voiceDictationAdvanced, checked)}
              testId="switch-user_features-voice-dictation-advanced-enable"
            />
          </div>
          <div className={styles.featuresItem}>
            <Switch
              name="Réferent en identitovigilance"
              checked={identityVigilanceOfficerEnabled}
              onChange={onIdentityVigilanceOfficerChange}
              testId="switch-user_features-identity-vigilance-officer-enable"
            />
          </div>
        </div>
      </FormBlock>
      <RoleRestriction roles={[Roles.ADMIN_TECH]}>
        <FormBlock label="Fonctionnalités Ségur - Danger zone" icon="forbidden">
          <div className={styles.features}>
            <div className={styles.dropdownItem}>
              <SelectInput
                title="Validation INSi"
                options={INSI_VALIDATION_OPTIONS}
                value={INSI_VALIDATION_OPTIONS.find(
                  ({ value }) => value === features.insiValidation,
                )}
                onSelect={(option) => onInsiValidationChange(option.value)}
                testId="user-features-insi_validation-enable"
              />
            </div>
            <div className={styles.featuresItem}>
              <Switch
                name="DMP Efficience"
                checked={features.dmpInApp}
                onChange={(checked) => onChange(FeaturesType.dmpInApp, checked)}
                testId="switch-user_features-efficience_dmp-enable"
              />
            </div>
            <div className={styles.featuresItem}>
              <Switch
                name="Récup auto INSi MSS"
                checked={features.mssAutoInsiFetch}
                onChange={(checked) => onChange(FeaturesType.mssAutoInsiFetch, checked)}
                testId="switch-user_features-mss-auto_insi-enable"
              />
            </div>
            <div className={styles.featuresItem}>
              <Switch
                name="CDA"
                checked={features.cda}
                onChange={(checked) => onChange(FeaturesType.cda, checked)}
                testId="switch-user_features-cda-enable"
              />
            </div>
            <div className={styles.featuresItem}>
              <Switch
                name="Nom de document au format SEGUR"
                checked={features.segurDocumentName}
                onChange={(checked) => onChange(FeaturesType.segurDocumentName, checked)}
                testId="switch-user_features-lap-enable"
              />
            </div>
            <div className={styles.featuresItem}>
              <Switch
                name="Extraction de patient via la MSS"
                checked={features.mssExtractPatient}
                onChange={(checked) => onChange(FeaturesType.mssExtractPatient, checked)}
                testId="switch-user_features-mss-extract_patient-enable"
              />
            </div>
          </div>
        </FormBlock>
      </RoleRestriction>
    </div>
  )
}
